import React, { useMemo, useRef } from 'react';
import Icon from '../Icon';
import ArchivedToggle from '../ui/ArchivedToggle';
import { TAB_SHORTCUTS } from '../../utils/taskFilters';
import FilterPopover, { FilterSectionSpec, FilterToggleSpec } from '../ui/FilterPopover';

export const DEFAULT_TASK_STATUS_FILTER = ['Not Started', 'In Progress'];

export const TASK_PRIORITY_OPTIONS = [
  { value: 'P0', label: 'P0 Critical' },
  { value: 'P1', label: 'P1 High' },
  { value: 'P2', label: 'P2 Medium' },
  { value: 'P3', label: 'P3 Low' },
];

const TASK_STATUS_OPTIONS = ['Not Started', 'In Progress', 'Done'].map(s => ({ value: s, label: s }));

interface TaskFiltersProps {
  myTasksOnly: boolean;
  onMyTasksToggle: () => void;
  priorityFilters: string[];
  onPriorityFiltersChange: (values: string[]) => void;
  assigneeFilters: string[];
  onAssigneeFiltersChange: (values: string[]) => void;
  assigneeOptions: string[];
  statusFilter: string[];
  onStatusFilterChange: (values: string[]) => void;
  archived: boolean;
  onArchivedChange: (on: boolean) => void;
  /** The stat-card shortcut currently narrowing the list (Overdue, P0/P1, Blocked), if any. */
  tabShortcut?: string;
  onTabShortcutClear?: () => void;
}

/**
 * The Tasks breadcrumb-bar filters: a "My Tasks" quick pill, one Filters
 * popover (Priority, Assignee, Status, plus "Assigned to me") and the shared
 * Archived switch. Replaces what used to be four separate controls.
 *
 * "My Tasks" and the popover's "Assigned to me" are the same state
 * (myTasksOnly), so they cannot drift apart, and it counts once in the badge.
 * Archived stays a switch in the bar like every other list; it is a view
 * choice (which records exist), not a narrowing filter, so it is not counted.
 */
export const TaskFilters: React.FC<TaskFiltersProps> = ({
  myTasksOnly, onMyTasksToggle,
  priorityFilters, onPriorityFiltersChange,
  assigneeFilters, onAssigneeFiltersChange, assigneeOptions,
  statusFilter, onStatusFilterChange,
  archived, onArchivedChange,
  tabShortcut, onTabShortcutClear,
}) => {
  // Handlers are read through a ref so the memoised specs below only change
  // when the data does (the bar passes fresh inline arrows every render).
  const h = useRef({ onPriorityFiltersChange, onAssigneeFiltersChange, onStatusFilterChange, onMyTasksToggle, onTabShortcutClear });
  h.current = { onPriorityFiltersChange, onAssigneeFiltersChange, onStatusFilterChange, onMyTasksToggle, onTabShortcutClear };
  const optionsKey = assigneeOptions.join('\u0000');

  const sections = useMemo<FilterSectionSpec[]>(() => [
    { key: 'priority', label: 'Priority', options: TASK_PRIORITY_OPTIONS, values: priorityFilters, onChange: v => h.current.onPriorityFiltersChange(v) },
    {
      key: 'assignee', label: 'Assignee', searchable: true,
      options: optionsKey ? optionsKey.split('\u0000').map(n => ({ value: n, label: n })) : [],
      values: assigneeFilters, onChange: v => h.current.onAssigneeFiltersChange(v),
    },
    {
      key: 'status', label: 'Status', options: TASK_STATUS_OPTIONS,
      values: statusFilter, onChange: v => h.current.onStatusFilterChange(v), defaultValues: DEFAULT_TASK_STATUS_FILTER,
    },
  ], [priorityFilters, optionsKey, assigneeFilters, statusFilter]);

  const shortcut = tabShortcut && TAB_SHORTCUTS.includes(tabShortcut) ? tabShortcut : undefined;
  const toggles = useMemo<FilterToggleSpec[]>(() => {
    const list: FilterToggleSpec[] = [{
      key: 'my-tasks', label: 'Assigned to me', chipLabel: 'My Tasks',
      checked: myTasksOnly,
      onChange: checked => { if (checked !== myTasksOnly) h.current.onMyTasksToggle(); },
    }];
    // A stat-card shortcut narrows the list too; surface it so it is visible
    // and cleared by Clear all, instead of hiding behind the cards.
    if (shortcut) {
      list.push({
        key: 'tab-shortcut', label: `Showing: ${shortcut}`, chipLabel: shortcut,
        checked: true, onChange: () => h.current.onTabShortcutClear?.(),
      });
    }
    return list;
  }, [myTasksOnly, shortcut]);

  return (
    <>
      <button
        type="button"
        className={`header-pill-tab${myTasksOnly ? ' active' : ''}`}
        onClick={onMyTasksToggle}
        aria-pressed={myTasksOnly}
        title="Show only tasks assigned to me"
      >
        <Icon name="user" size={13} />
        My Tasks
      </button>
      <FilterPopover sections={sections} toggles={toggles} />
      <div className="header-filter-group">
        <ArchivedToggle checked={archived} onChange={onArchivedChange} />
      </div>
    </>
  );
};

export default TaskFilters;
